import React, { useState } from "react";
import { ChevronRight, Search, Heart, Brain, Activity, ArrowRight } from "lucide-react";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import MedicalSpecialtiesFrame from "../assets/MedicalSpecialtiesFrame.png";
import {
    FaUserMd,
    FaBrain,
    FaHeartbeat,
    FaXRay,
    FaHospital,
    FaHandHoldingMedical,
    FaLungs,
    FaBone,
    FaFlask,
    FaVial,
    FaDna,
    FaProcedures,
    FaWheelchair,
    FaTeeth
} from "react-icons/fa";
import { MdAccessibilityNew } from "react-icons/md";

// Custom icons mapping for different specialties
const getSpecialtyIcon = (specialty) => {
  const iconMap = {
    "Orthopaedics": <MdAccessibilityNew size={24} className="text-blue-500" />,
    "Neuro Surgery": <FaBrain size={24} className="text-purple-500" />,
    "Anesthesia & Critical Care": <FaProcedures size={24} className="text-red-500" />,
    "Plastic Surgery": <FaHandHoldingMedical size={24} className="text-pink-500" />,
    "Trauma Surgery": <FaUserMd size={24} className="text-orange-500" />,
    "Oral & Maxillofacial Surgery": <FaTeeth size={24} className="text-yellow-500" />,
    "Psychiatry": <Brain size={24} className="text-indigo-500" strokeWidth={1.5} />,
    "Bone Marrow Transplant": <FaDna size={24} className="text-green-500" />,
    "Physiotherapy": <FaWheelchair size={24} className="text-teal-500" />,
    "Cosmetic Surgery": <Heart size={24} className="text-rose-500" strokeWidth={1.5} />,
    "Hyperbaric Oxygen Therapy": <FaLungs size={24} className="text-sky-500" />,
    "Rehabilitation Care": <Activity size={24} className="text-emerald-500" strokeWidth={1.5} />,
    "Radiology": <FaXRay size={24} className="text-gray-500" />,
    "Joint Replacement": <MdAccessibilityNew size={24} className="text-blue-400" />,
    "Laboratory Services": <FaFlask size={24} className="text-amber-500" />,
    "Blood Centre": <FaVial size={24} className="text-red-600" />,
    "ECMO": <FaHeartbeat size={24} className="text-red-400" />
  };
  
  return iconMap[specialty] || <FaUserMd size={24} className="text-teal-500" />;
};

const specialties = {
    "Centers Of Excellence": [
        { label: "Orthopaedics", description: "Comprehensive care for bone, joint, and spine conditions with advanced surgical interventions" },
        { label: "Neuro Surgery", description: "World-class surgical care for brain, spine, and peripheral nerve disorders" },
        { label: "Anesthesia & Critical Care", description: "24/7 state-of-the-art critical care services and anesthesia management" },
        { label: "Plastic Surgery", description: "Reconstructive and aesthetic procedures by expert surgeons" },
        { label: "Trauma Surgery", description: "Immediate surgical intervention for traumatic injuries and accident cases" },
        { label: "Oral & Maxillofacial Surgery", description: "Specialized surgery for face, mouth, and jaw conditions" },
        { label: "Psychiatry", description: "Comprehensive mental health care and psychological support services" },
        { label: "Bone Marrow Transplant", description: "Advanced transplantation procedures for various hematological disorders" },
    ],
    "Other Medical Services": [
        { label: "Physiotherapy", description: "Rehabilitation services to restore movement and function" },
        { label: "Cosmetic Surgery", description: "Aesthetic procedures to enhance appearance and boost confidence" },
        { label: "Hyperbaric Oxygen Therapy", description: "Specialized treatment using pressurized oxygen for wound healing" },
        { label: "Rehabilitation Care", description: "Customized programs to restore functions after injury or surgery" },
        { label: "Radiology", description: "Advanced diagnostic imaging including MRI, CT, and ultrasound" },
        { label: "Joint Replacement", description: "State-of-the-art replacement surgeries for knee, hip, and shoulder" },
        { label: "Laboratory Services", description: "Comprehensive diagnostic testing with quick and accurate results" },
        { label: "Blood Centre", description: "Blood banking and transfusion services with rigorous quality control" },
        { label: "ECMO", description: "Extracorporeal membrane oxygenation for critical cardiopulmonary support" },
    ],
};

// Featured specialty data
const featuredSpecialties = [
  {
    name: "Trauma Surgery",
    icon: <FaUserMd size={40} />,
    description: "Immediate surgical intervention for critical trauma cases with 24/7 availability",
    stats: ["98% Success Rate", "24/7 Availability", "15+ Specialists"]
  },
  {
    name: "Orthopaedics",
    icon: <MdAccessibilityNew size={40} />,
    description: "Comprehensive orthopedic care including joint replacements, spine surgery, and sports medicine",
    stats: ["5000+ Surgeries", "Advanced Robotics", "Minimal Recovery Time"]
  },
  {
    name: "Plastic Surgery",
    icon: <FaUserMd size={40} />,
    description: "Advanced reconstructive and cosmetic procedures performed by experienced plastic surgeons",
    stats: ["Reconstructive Surgery", "Burn Care", "Cosmetic Enhancements"]
  }  
];

const MedicalSpecialty = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState("All");
    const [hoveredSpecialty, setHoveredSpecialty] = useState(null);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const categories = ["All", ...Object.keys(specialties)];

    // Filter specialties based on search and category
    const getFilteredSpecialties = () => {
        let filtered = {};
        
        if (activeCategory === "All") {
            filtered = {...specialties};
        } else {
            filtered = {[activeCategory]: specialties[activeCategory]};
        }
        
        // If search query exists, filter further
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            Object.keys(filtered).forEach(category => {
                filtered[category] = filtered[category].filter(item => 
                    item.label.toLowerCase().includes(query)
                );
            });
        }
        
        return filtered;
    };

    const filteredSpecialties = getFilteredSpecialties();

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />

            {/* Hero Banner */}
            <section
                className="relative bg-center bg-cover h-[550px]"
                style={{ backgroundImage: `url(${MedicalSpecialtiesFrame})` }}
                aria-label="Medical Specialties Banner"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-blue-900/40" />
                
                <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                    <div className="max-w-3xl">
                        <h1 className="mb-4 text-4xl font-bold text-white md:text-5xl">
                            World-Class Medical Specialties
                        </h1>
                        <p className="mb-8 text-xl text-white/90">
                            Discover our comprehensive range of specialized medical services designed to provide exceptional care for every patient
                        </p>
                    </div>
                </div>
                
                {/* Search Box */}
                <div className="absolute bottom-[-80px] left-1/2 transform -translate-x-1/2 w-[90%] md:w-[80%] lg:w-[70%] z-20">
                    <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100">
                        <h2 className="mb-4 text-xl font-semibold text-gray-800 text-center">
                            Find a Medical Specialty
                        </h2>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_auto]">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    placeholder="Search by specialty name or keyword"
                                    className="w-full py-3 pl-10 pr-4 text-gray-700 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
                                    aria-label="Search Specialty"
                                />
                                <Search
                                    className="absolute text-gray-500 transform -translate-y-1/2 left-3 top-1/2"
                                    size={18}
                                />
                            </div>

                            <div className="relative">
                                <select
                                    className="w-full py-3 pl-10 pr-4 text-gray-700 bg-gray-100 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
                                    aria-label="Select Hospital"
                                >
                                    <option>Trauma Centre (IMS BHU)</option>
                                </select>
                                <FaHospital className="absolute text-gray-500 transform -translate-y-1/2 left-3 top-1/2" size={18} />
                                <ChevronRight className="absolute text-gray-500 transform -translate-y-1/2 rotate-90 right-3 top-1/2" size={18} />
                            </div>

                            <button
                                type="button"
                                className="px-6 py-3 font-medium text-white transition-all bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg hover:shadow-lg hover:opacity-95"
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Specialties */}
            {/* <section className="py-24 bg-white">
                <div className="px-6 mx-auto max-w-7xl">
                    <div className="mb-12 text-center">
                        <span className="px-3 py-1 text-sm font-medium text-teal-700 bg-teal-100 rounded-full">
                            Top Specialties
                        </span>
                        <h2 className="mt-4 text-3xl font-bold text-gray-900">Our Featured Medical Services</h2>
                        <p className="mt-2 text-lg text-gray-600">
                            Exceptional care delivered by experienced specialists using cutting-edge technology
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                        {featuredSpecialties.map((specialty, idx) => (
                            <div key={idx} className="relative p-6 overflow-hidden bg-white border border-gray-100 rounded-xl hover:shadow-lg transition-all group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-teal-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
                                
                                <div className="flex items-center justify-center w-16 h-16 mb-4 text-teal-600 bg-teal-100 rounded-full">
                                    {specialty.icon}
                                </div>
                                
                                <h3 className="mb-2 text-xl font-bold text-gray-900">{specialty.name}</h3>
                                <p className="mb-6 text-gray-600">{specialty.description}</p>
                                
                                <div className="grid grid-cols-1 gap-2 mb-6">
                                    {specialty.stats.map((stat, i) => (
                                        <div key={i} className="flex items-center">
                                            <div className="w-1 h-5 mr-3 bg-teal-500 rounded-full"></div>
                                            <span className="text-sm font-medium text-gray-700">{stat}</span>
                                        </div>
                                    ))}
                                </div>
                                
                                <button className="flex items-center text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Learn More <ArrowRight size={16} className="ml-1" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* Category Tabs */}
            <section className="pt-16 pb-20 bg-gray-50 mt-12">
                <div className="px-6 mx-auto max-w-7xl">
                    <div className="mb-8 overflow-x-auto">
                        <div className="flex space-x-2 min-w-max">
                            {categories.map((category, idx) => (
                                <button
                                    key={idx}
                                    className={`px-5 py-2 text-sm font-medium rounded-lg transition-all ${
                                        activeCategory === category
                                            ? "bg-teal-600 text-white shadow-md"
                                            : "bg-white text-gray-700 hover:bg-gray-100"
                                    }`}
                                    onClick={() => setActiveCategory(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Specialties Grid */}
                    {Object.entries(filteredSpecialties).map(([category, items], index) => (
                        <div key={index} className="mb-12">
                            <h3 className="flex items-center mb-6 text-xl font-bold text-gray-800">
                                {category}
                                <span className="ml-2 px-2 py-0.5 text-sm bg-teal-100 text-teal-700 rounded-full">
                                    {items.length}
                                </span>
                            </h3>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {items.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="relative p-5 overflow-hidden transition-all bg-white border border-gray-100 shadow-sm hover:shadow-md rounded-xl hover:border-teal-200"
                                        onMouseEnter={() => setHoveredSpecialty(`${category}-${idx}`)}
                                        onMouseLeave={() => setHoveredSpecialty(null)}
                                    >
                                        <div className="flex items-start mb-3">
                                            <div className="p-2 mr-4 rounded-lg bg-gray-50">
                                                {getSpecialtyIcon(item.label)}
                                            </div>
                                            <div>
                                                <h4 className="text-lg font-semibold text-gray-900">
                                                    {item.label}
                                                </h4>
                                                {hoveredSpecialty === `${category}-${idx}` && (
                                                    <p className="mt-1 text-sm text-gray-600 animate-fadeIn">
                                                        {item.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center justify-between mt-4">
                                            <button className="text-sm text-teal-600 hover:text-teal-700">
                                                View Details
                                            </button>
                                            <div className="p-1 text-gray-400 rounded-full hover:bg-gray-100">
                                                <ChevronRight size={16} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-cyan-700 to-teal-600">
                <div className="px-6 mx-auto max-w-7xl">
                    <div className="flex flex-col items-center max-w-3xl mx-auto text-center">
                        <h2 className="mb-4 text-3xl font-bold text-white">Need Help Finding the Right Specialty?</h2>
                        <p className="mb-8 text-lg text-teal-100">
                            Our medical team is here to guide you to the most appropriate specialty for your condition. Schedule a consultation today.
                        </p>
                        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:space-x-4">
                            <button className="px-8 py-3 font-medium transition-all border border-white rounded-lg text-teal-50 hover:bg-teal-500">
                                Call Helpline: +91 0542–2366073
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-white">
                <div className="px-6 mx-auto max-w-7xl">
                    <div className="max-w-3xl mx-auto mb-12 text-center">
                        <span className="px-3 py-1 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
                            FAQs
                        </span>
                        <h2 className="mt-4 text-3xl font-bold text-gray-900">Common Questions</h2>
                        <p className="mt-2 text-lg text-gray-600">
                            Find answers to frequently asked questions about our medical specialties
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {[
                            {
                                q: "How do I choose the right medical specialty?",
                                a: "Our specialists can help determine the most appropriate specialty based on your symptoms, medical history, and diagnostic tests. You can also schedule a general consultation for guidance."
                            },
                            {
                                q: "Are appointments required for all specialties?",
                                a: "While we recommend scheduling appointments for better service, our emergency and trauma services are available 24/7 without prior appointment."
                            },
                            {
                                q: "What insurance plans are accepted?",
                                a: "We accept most major insurance plans and government health schemes. Our billing department can verify your coverage before treatment."
                            },
                            {
                                q: "How can I get a referral to a specialist?",
                                a: "You can get a referral from your primary care physician or directly schedule an appointment with our specialists depending on your condition."
                            },
                            {
                                q: "Are virtual consultations available?",
                                a: "Yes, we offer telemedicine services for many of our specialties. Please contact our appointment desk for availability."
                            },
                            {
                                q: "What should I bring to my first appointment?",
                                a: "Please bring your ID, insurance information, medical records, list of current medications, and any relevant test results."
                            }
                        ].map((faq, idx) => (
                            <div key={idx} className="p-6 bg-gray-50 rounded-xl">
                                <h3 className="mb-3 text-lg font-semibold text-gray-800">{faq.q}</h3>
                                <p className="text-gray-600">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
            
            {/* Add some CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default MedicalSpecialty;